import type { ReactNode } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import { EmptyState, ErrorState, LoadingState } from './states';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** Right-aligned digits with tabular figures — for counts and money, which are read by comparison. */
  numeric?: boolean;
  render: (row: T) => ReactNode;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string | number;
  caption: string;
  isLoading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: ReactNode;
}

/**
 * The console table.
 *
 * A `<caption>` is always present — visually hidden, but it is how a screen-reader user knows what the
 * table holds before stepping into it. The wrapper scrolls horizontally on its own so that a wide table on
 * a narrow screen never makes the page body scroll sideways.
 *
 * There is deliberately no row-level click handler. A clickable `<tr>` cannot be reached by keyboard or
 * announced as actionable, so a table that opens a record puts a real button or link in one of its cells
 * (the `dg-row-button` class) and that control carries the row's name.
 */
export function DataTable<T>({
  columns, rows, rowKey, caption, isLoading, error, onRetry, empty,
}: DataTableProps<T>) {
  const { t } = useI18n();

  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (isLoading && !rows) return <LoadingState />;
  if (rows && rows.length === 0) {
    return <>{empty ?? <EmptyState icon="📭" title={t('state.empty')} />}</>;
  }

  return (
    <div className="dg-table-wrap">
      <table className="dg-table">
        <caption className="dg-visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col">{column.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(rows ?? []).map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => (
                <td key={column.key} className={column.numeric ? 'dg-td--numeric' : undefined}>
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
